import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import { Button } from '@/components/ui'
import type { BillingStatusResponse } from '@spoofio/shared/types'

type AddOnManagerProps = {
  billingStatus: BillingStatusResponse
}

export function AddOnManager({ billingStatus }: AddOnManagerProps) {
  const { orgId } = useCurrentOrgId()
  const queryClient = useQueryClient()
  const { plan, usage } = billingStatus
  const isActive = billingStatus.organization.status === 'active'

  const invalidateBilling = () => {
    void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
  }

  const addDomain = useMutation({
    mutationFn: () => api.addDomainAddon(orgId ?? ''),
    onSuccess: invalidateBilling,
  })

  const removeDomain = useMutation({
    mutationFn: () => api.removeDomainAddon(orgId ?? ''),
    onSuccess: invalidateBilling,
  })

  const addCanary = useMutation({
    mutationFn: () => api.addCanaryAddon(orgId ?? ''),
    onSuccess: invalidateBilling,
  })

  const removeCanary = useMutation({
    mutationFn: () => api.removeCanaryAddon(orgId ?? ''),
    onSuccess: invalidateBilling,
  })

  const canRemoveDomain = plan.additionalDomains > 0
    && usage.currentDomainCount < plan.effectiveDomainLimit
  const canRemoveCanary = plan.additionalCanaries > 0
    && usage.currentCanaryCount < plan.effectiveCanaryLimit

  return (
    <div className="card">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Add-Ons
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6 space-y-6">
        {/* Domain Add-ons */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Additional Domains
              </h4>
              <p className="text-xs text-gray-500">
                {plan.additionalDomains} add-on{plan.additionalDomains !== 1 ? 's' : ''} &middot; $99/mo each
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!isActive || !canRemoveDomain || removeDomain.isPending}
                isLoading={removeDomain.isPending}
                onClick={() => { removeDomain.mutate() }}
              >
                Remove
              </Button>
              <Button
                size="sm"
                disabled={!isActive || addDomain.isPending}
                isLoading={addDomain.isPending}
                onClick={() => { addDomain.mutate() }}
              >
                Add Domain
              </Button>
            </div>
          </div>
          {removeDomain.isError && (
            <p className="text-xs text-danger-600 mt-1">
              Cannot remove domain add-on: all domains are in use.
            </p>
          )}
        </div>

        {/* Canary Add-ons */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Additional Canaries
              </h4>
              <p className="text-xs text-gray-500">
                {plan.additionalCanaries} add-on{plan.additionalCanaries !== 1 ? 's' : ''} &middot; $49/mo each
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!isActive || !canRemoveCanary || removeCanary.isPending}
                isLoading={removeCanary.isPending}
                onClick={() => { removeCanary.mutate() }}
              >
                Remove
              </Button>
              <Button
                size="sm"
                disabled={!isActive || addCanary.isPending}
                isLoading={addCanary.isPending}
                onClick={() => { addCanary.mutate() }}
              >
                Add Canary
              </Button>
            </div>
          </div>
          {removeCanary.isError && (
            <p className="text-xs text-danger-600 mt-1">
              Cannot remove canary add-on: all canary slots are in use.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
